import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input, SimpleChange, SimpleChanges } from '@angular/core';
import { Validators } from '@angular/forms';

import { DynamicFormComponent } from '@shared/dynamic-form/dynamic-form.component';

import { SortService } from '@services/sort.service';
import { filter, take } from 'rxjs';
import { Utils } from '@json/src/app/Utils';

@Component({
    selector: 'app-patient-edit-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss']
})
export class GeneralComponent extends DynamicFormComponent implements OnInit, OnDestroy {

    @Input() cipaPattern: string | undefined;
    buttonCancel: boolean = false
    deregistrationDateCancellable: boolean = true;

    private medicalDataStatusAtHomeFieldControl: any;
    private medicalDataStatusPrivateFieldControl: any;
    private medicalDataStatusMutuasFieldControl: any;
    private medicalDataStatusExternalPharmacyFieldControl: any;

    constructor(
        protected override sortService: SortService,
        protected override cdr: ChangeDetectorRef
    ) {
        super(sortService, cdr);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // Disable the deregistration date and reason fields if the reason is "Death"
        this.schema[2].fieldControl.valueChanges
            .pipe(take(1))
            .subscribe((data) => {
                if ( data.deregistrationReason === 0 ) {
                    // @ts-ignore
                    this.schema[2].childs[1].fieldControl.disable();
                    // @ts-ignore
                    this.schema[2].childs[2].fieldControl.disable();
                    this.deregistrationDateCancellable = false;
                }
            })

        this.subs.push(
            this.schema[2].fieldControl.valueChanges.subscribe((data) => {
                if (data.deregistrationDate !== null && data.deregistrationReason !== 0) {
                    this.buttonCancel = true
                }

                if ( data.deregistrationReason === 0 ) {
                    // @ts-ignore
                    this.schema[2].childs[1].maxDate = Utils.NOW;
                } else {
                    // @ts-ignore
                    this.schema[2].childs[1].maxDate = null;
                }
            })
        )

        // Get the CIPA field
        const cipa = this.schema.find(schema =>
            schema.childs?.some(child => child.label === "CIPA")
        )?.childs?.find(child => child.label === "CIPA");

        if (cipa) {
            // Uppercase the CIPA field
            this.subs.push(
                cipa.fieldControl.valueChanges
                .pipe(filter((val) => !!val))
                .subscribe((val) => {
                    cipa.fieldControl.setValue(val.toUpperCase(), { emitEvent: false, onlySelf: true });
                })
            );
        }

        this.medicalDataStatusAtHomeFieldControl = this.schema.find(schema => schema.label === "Datos médicos")?.childs?.find(child => child.label === "Estados")?.fieldControl.get('at_home');
        this.medicalDataStatusPrivateFieldControl = this.schema.find(schema => schema.label === "Datos médicos")?.childs?.find(child => child.label === "Estados")?.fieldControl.get('private');
        this.medicalDataStatusMutuasFieldControl = this.schema.find(schema => schema.label === "Datos médicos")?.childs?.find(child => child.label === "Estados")?.fieldControl.get('mutuas_muface');
        this.medicalDataStatusExternalPharmacyFieldControl = this.schema.find(schema => schema.label === "Datos médicos")?.childs?.find(child => child.label === "Estados")?.fieldControl.get('external_pharmacy');
        const medicalDataStatusSelfManagedFieldControl = this.schema.find(schema => schema.label === "Datos médicos")?.childs?.find(child => child.label === "Estados")?.fieldControl.get('self_managed');

        if (this.medicalDataStatusAtHomeFieldControl && medicalDataStatusSelfManagedFieldControl) {
            this.subs.push(
                this.medicalDataStatusAtHomeFieldControl.valueChanges.subscribe((val: any) => {
                    if ( val ) {
                        medicalDataStatusSelfManagedFieldControl.setValue(false, { emitEvent: false });
                    }
                })
            )
            this.subs.push(
                medicalDataStatusSelfManagedFieldControl.valueChanges.subscribe((val: any) => {
                    if ( val ) {
                        this.medicalDataStatusAtHomeFieldControl.setValue(false, { emitEvent: false });
                    }
                })
            )
        }

        // Subscribe to medical data status changes to update CIPA validators
        this.subscribeToCipaValidation();

        this.schema.forEach(schema => schema.childs?.forEach(child => { this.fieldVisible(child, child) }));
    }

    /**
     * Comprueba si el campo CIPA es obligatorio.
     * El CIPA será obligatorio cuando NINGUNO de los siguientes campos sea true:
     * - medicalDataStatusAtHomeFieldControl
     * - medicalDataStatusPrivateFieldControl
     * - medicalDataStatusMutuasFieldControl
     * - medicalDataStatusExternalPharmacyFieldControl
     *
     * @returns true si el CIPA es obligatorio, false en caso contrario
     */
    isCipaRequired(): boolean {
        const atHome = this.medicalDataStatusAtHomeFieldControl?.value || false;
        const privateStatus = this.medicalDataStatusPrivateFieldControl?.value || false;
        const mutuas = this.medicalDataStatusMutuasFieldControl?.value || false;
        const externalPharmacy = this.medicalDataStatusExternalPharmacyFieldControl?.value || false;

        // Si al menos uno está activo, el CIPA NO es obligatorio
        // Si ninguno está activo, el CIPA SÍ es obligatorio
        return !(atHome || privateStatus || mutuas || externalPharmacy);
    }

    /**
     * Suscribe a los cambios de valor de los controles de estado médico
     * para actualizar dinámicamente los validadores del campo CIPA
     */
    private subscribeToCipaValidation(): void {
        // const cipa = this.schema.find(schema => schema.label === "Datos Sanitarios")?.childs?.find(child => child.label === "CIPA")?.fieldControl.get('mutuas_muface')
        const cipa = this.schema.find(schema =>
            schema.childs?.some(child => child.label === "CIPA")
        )?.childs?.find(child => child.label === "CIPA");

        if (!cipa) {
            return;
        }

        const updateCipaValidators = () => {
            if (this.isCipaRequired()) {
                // El CIPA es obligatorio
                cipa.fieldControl.setValidators([
                    Validators.required,
                    // @ts-ignore
                    Validators.pattern(this.cipaPattern),
                ]);

                cipa.fieldControl.markAllAsTouched();
                cipa.fieldControl.updateValueAndValidity();
            } else {
                // El CIPA NO es obligatorio
                cipa.fieldControl.clearValidators();
                cipa.fieldControl.setErrors(null);
                cipa.fieldControl.markAsUntouched();
                cipa.fieldControl.updateValueAndValidity();
            }

            // Actualizar el FormGroup padre (medicalCard) para que refleje el cambio
            const medicalCardControl = this.schema.find(schema =>
                schema.childs?.some(child => child.label === "CIPA")
            )?.fieldControl;

            if (medicalCardControl) {
                medicalCardControl.updateValueAndValidity();
            }
        };

        // Suscribirse a cambios en los 4 controles de estado médico
        if (this.medicalDataStatusAtHomeFieldControl) {
            this.subs.push(
                this.medicalDataStatusAtHomeFieldControl.valueChanges.subscribe(() => {
                    updateCipaValidators();
                })
            );
        }

        if (this.medicalDataStatusPrivateFieldControl) {
            this.subs.push(
                this.medicalDataStatusPrivateFieldControl.valueChanges.subscribe(() => {
                    updateCipaValidators();
                })
            );
        }

        if (this.medicalDataStatusMutuasFieldControl) {
            this.subs.push(
                this.medicalDataStatusMutuasFieldControl.valueChanges.subscribe(() => {
                    updateCipaValidators();
                })
            );
        }

        if (this.medicalDataStatusExternalPharmacyFieldControl) {
            this.subs.push(
                this.medicalDataStatusExternalPharmacyFieldControl.valueChanges.subscribe(() => {
                    updateCipaValidators();
                })
            );
        }

        // Ejecutar una vez al inicio para establecer el estado inicial
        updateCipaValidators();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    cancelDeregistrationDate() {

        this.schema.forEach(schema => schema.childs?.forEach(child => {
            if (child.label === 'Baja') {
                child.fieldControl.setValue(null);

            }
            if (child.label === 'Motivo de baja') {
                child.fieldControl.setValue(null);
            }
        }));
        this.buttonCancel = false
    }
    ngOnChanges(changes: SimpleChanges): void {
        /*
         * This is a workaround to set the validators of the CIPA field
         */

        // @ts-ignore
        if (changes.cipaPattern) {
            const privateInsurance = this.schema.find(schema =>
                schema.childs?.some(child => child.label === "Seguro privado")
            )?.childs?.find(child => child.label === "Seguro privado");

            const cipa = this.schema.find(schema =>
                schema.childs?.some(child => child.label === "CIPA")
            )?.childs?.find(child => child.label === "CIPA");

            cipa?.fieldControl.setValidators([
                Validators.required,
                // @ts-ignore
                Validators.pattern(this.cipaPattern),
            ])
            cipa?.fieldControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

            privateInsurance?.fieldControl.valueChanges.subscribe((val) => {
                if (val != undefined && cipa?.fieldControl.value == undefined) {
                    cipa?.fieldControl.setValidators([]);
                    cipa?.fieldControl.setErrors(null);
                }
                else if (val == undefined) {
                    cipa?.fieldControl.setValidators([
                        Validators.required,
                        // @ts-ignore
                        Validators.pattern(this.cipaPattern),
                    ])
                }
                cipa?.fieldControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            });
            // privateInsurance?.fieldControl.valueChanges
            //     .pipe(
            //         skipWhile((value) => value === null),
            //         take(1)
            //     )
            //     .subscribe((value) => {
            //         if (!value || !!cipa?.fieldControl.value) {
            //             cipa?.fieldControl.setValidators([
            //                 Validators.required,
            //                 // @ts-ignore
            //                 Validators.pattern(this.cipaPattern),
            //             ])
            //             cipa?.fieldControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            //         } else {
            //             cipa?.fieldControl.setValidators([]);
            //             cipa?.fieldControl.setErrors(null);
            //         }
            //     });
        }
    }
}
